/** Want it sooner / in-stock tile image — Figma 317×400 (portrait), no radius on image frame. */
const TILE_IMAGE_W = 317;
const TILE_IMAGE_H = 400;

/** Sale / CMS marketing pills — 12px Aktiv SemiBold. */
const INSTOCK_TILE_INFO_BADGE_BASE = {
  alignItems: 'center',
  borderRadius: '4px',
  color: '#FFFFFF',
  display: 'flex',
  gap: '8px',
  fontFamily: 'heading',
  fontSize: '12px',
  fontStyle: 'normal',
  fontWeight: 500,
  h: {base: 'auto', lg: '22px'},
  letterSpacing: 0,
  lineHeight: '120%',
  minH: {base: 'auto', lg: '22px'},
  paddingX: '8px',
  paddingY: '4px',
  textTransform: 'none',
};

const InstockProductTile = {
  parts: [
    'container',
    'link',
    'imageWrapper',
    'aspectRatio',
    'swiperContainer',
    'placeholderWrapper',
    'placeholderImage',
    'instockBadge',
    'infoSection',
    'heroBadgeStack',
    'infoSaleBadge',
    'infoMarketingBadge',
    'titleDescriptionGroup',
    'badgeText',
    'title',
    'description',
    'priceSection',
    'priceDiscountRow',
    'priceValue',
    'priceValueSale',
    'priceOriginalLabel',
    'priceStrike'
  ],
  baseStyle: {
    container: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
    },
    link: {
      borderRadius: 'sm',
      display: 'block',
      textDecoration: 'none',
      _focusVisible: {
        outline: '2px solid',
        outlineColor: 'brandColors.primaryDarkLinen',
        outlineOffset: '2px',
      },
      _hover: {textDecoration: 'none'},
    },
    imageWrapper: {
      position: 'relative',
      overflow: 'hidden',
      borderRadius: 'unset',
      sx: {
        '[data-kl-instock-variant="wantItSooner"] &': {
          bg: 'brandColors.primaryLinen15',  
        },
      }
    },
    aspectRatio: {
      ratio: TILE_IMAGE_W / TILE_IMAGE_H,
      maxW: `${TILE_IMAGE_W}px`,
      w: '100%',
    },
    swiperContainer: {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
    },
    // "Image not available" placeholder — shown when a product has no configured imagery.
    // Mirrors the PLP `RangeProductTile` placeholder so the affordance reads consistently.
    placeholderWrapper: {
      alignItems: 'center',
      bg: 'transparent',
      display: 'flex',
      height: '100%',
      justifyContent: 'center',
      width: '100%',
    },
    placeholderImage: {
      height: 'auto',
      maxHeight: '60%',
      maxWidth: '60%',
      objectFit: 'contain',
      userSelect: 'none',
    },

    // Green stock pill — lives inside `heroBadgeStack`; order enforced in JSX (Want It Sooner: first).
    instockBadge: {
      alignItems: 'center',
      backgroundColor: 'success.background',
      borderRadius: '4px',
      boxSizing: 'border-box',
      color: 'success.dark',
      display: 'inline-flex',
      flexShrink: 0,
      fontFamily: 'body',
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: 600,
      gap: 2,
      h: '22px',
      justifyContent: 'center',
      lineHeight: 1.2,
      minW: '53px',
      p: 1,
      textTransform: 'none',
    },
    infoSection: {
      alignItems: 'flex-start',
      alignSelf: 'stretch',
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      paddingY: 4,
      paddingX: {base: 0, lg: 4},
    },
    // Overlaid hero cluster (`range-product-tile-badges` coords).
    heroBadgeStack: {
      alignSelf: 'flex-start',
      alignItems: 'flex-start',
      display: 'flex',
      flexFlow: 'row wrap',
      gap: 2,
      left: {base: '12px', lg: '20px'},
      pointerEvents: 'none',
      position: 'absolute',
      top: {base: '12px', lg: '20px'},
      width: 'auto',
      zIndex: 2,
      '> *': {
        pointerEvents: 'auto',
      },
    },
    infoSaleBadge: {
      ...INSTOCK_TILE_INFO_BADGE_BASE,
      bg: 'secondary.newSaleRed',
    },
    infoMarketingBadge: {
      ...INSTOCK_TILE_INFO_BADGE_BASE,
      bg: 'brandColors.greyscale90',
    },
    titleDescriptionGroup: {
      alignItems: 'flex-start',
      alignSelf: 'stretch',
      display: 'flex',
      flexDirection: 'column',
      gap: {base: 3, lg: 4},
      width: '100%',
    },
    badgeText: {
      backgroundColor: 'brandColors.greyscale90',
      borderRadius: 'base',
      color: 'white',
      display: 'inline-block',
      fontFamily: 'heading',
      fontSize: '12px',
      fontWeight: 600,
      lineHeight: 1.2,
      maxWidth: 'fit-content',
      paddingX: 1.5,
      paddingY: 0.5,
      textTransform: 'uppercase',
    },
    title: {
      color: 'grayAlpha.90',
      fontFamily: 'heading',
      fontSize: {base: '14px', lg: '18px'},
      fontStyle: 'normal',
      fontVariantNumeric: 'lining-nums proportional-nums',
      fontWeight: 500,
      lineHeight: 1.2,
      noOfLines: 3,
    },
    description: {
      color: 'greyscale.70',
      fontFamily: 'body',
      fontSize: {base: '12px', lg: '14px'},
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: 1.4,
      noOfLines: 2,
    },
    priceSection: {
      alignItems: {base: 'flex-start', lg: 'center'},
      display: 'flex',
      flexDirection: {base: 'column', lg: 'row'},
      flexWrap: 'wrap',
      gap: {base: 1, lg: 2},
      marginTop: 0,
    },

    priceDiscountRow: {
      alignItems: 'center',
      display: 'inline-flex',
      flex: '0 0 auto',
      flexWrap: 'nowrap',
      gap: '3px',
    },
    priceValue: {
      color: 'grayAlpha.90',
      fontFamily: 'heading',
      fontSize: {base: '14px', lg: '16px'},
      fontStyle: 'normal',
      fontVariantNumeric: 'lining-nums proportional-nums',
      fontWeight: 500,
      lineHeight: 1.2,
    },
    priceValueSale: {
      color: 'secondary.newSaleRed',
      fontFamily: 'heading',
      fontSize: {base: '14px', lg: '16px'},
      fontStyle: 'normal',
      fontVariantNumeric: 'lining-nums proportional-nums',
      fontWeight: 500,
      lineHeight: 1.2,
    },
    priceOriginalLabel: {
      color: 'greyscale.70',
      fontFamily: 'body',
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1.2,
      whiteSpace: 'nowrap',
    },
    priceStrike: {
      color: 'greyscale.70',
      fontFamily: 'body',
      fontSize: '12px',
      fontStyle: 'normal',
      fontWeight: 400,
      lineHeight: 1.2,
      textDecoration: 'none',
      whiteSpace: 'nowrap',
    },
  },
  variants: {
    default: {},

    /**
     * Want It Sooner carousel only — keeps legacy typography; isolated from CF product-tile specs.
     */
    wantItSooner: {
      badgeText: {
        backgroundColor: 'brandColors.greyscale90',
        borderRadius: 'base',
        color: 'white',
        display: 'inline-block',
        fontFamily: 'body',
        fontSize: 'xs',
        fontWeight: 600,
        lineHeight: 1.2,
        maxWidth: 'fit-content',
        paddingX: 1.5,
        paddingY: 0.5,
        textTransform: 'uppercase',
      },
      title: {
        color: 'grayAlpha.90',
        fontFamily: 'body',
        fontSize: {base: 'md', lg: 'lg'},
        fontStyle: 'normal',
        fontVariantNumeric: 'lining-nums proportional-nums',
        fontWeight: 500,
        lineHeight: 1.2,
        noOfLines: 3,
      },
      description: {
        color: 'greyscale.70',
        fontFamily: 'body',
        fontSize: {base: 'sm', lg: 'xs'},
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: {base: 1.4, lg: 1.2},
        noOfLines: 2,
      },
      priceValue: {
        color: 'grayAlpha.90',
        fontFamily: 'body',
        fontSize: 'md',
        fontStyle: 'normal',
        fontVariantNumeric: 'lining-nums proportional-nums',
        fontWeight: 500,
        lineHeight: 1.2,
      },
      priceOriginalLabel: {
        color: 'greyscale.70',
        fontFamily: 'body',
        fontSize: 'xs',
        fontWeight: 400,
        lineHeight: 1.2,
        whiteSpace: 'nowrap',
      },
      priceStrike: {
        color: 'greyscale.70',
        fontFamily: 'body',
        fontSize: 'xs',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 1.2,
        textDecoration: 'line-through',
        whiteSpace: 'nowrap',
      },
    },

  },
  defaultProps: {
    variant: 'default',
  },
};

export default InstockProductTile;
